const ORIGINAL_SIZE_ATTR = 'data-a11y-original-font-size';

/**
 * Campaign blocks use authored inline pixel sizes, which do not respond when
 * the root font size changes. Scale only those explicit sizes inside <main>.
 * The original value is retained on the element so reset is exact and does
 * not mutate the campaign or its saved layout.
 */
export function scalePublicPixelText(root: ParentNode, scale: number): void {
  root.querySelectorAll<HTMLElement>('*').forEach((element) => {
    const saved = element.getAttribute(ORIGINAL_SIZE_ATTR);
    const inline = element.style.fontSize.trim();

    if (scale <= 1) {
      if (saved !== null) {
        element.style.fontSize = saved;
        element.removeAttribute(ORIGINAL_SIZE_ATTR);
      }
      return;
    }

    if (saved === null && /^\d+(?:\.\d+)?px$/i.test(inline)) {
      element.setAttribute(ORIGINAL_SIZE_ATTR, inline);
    }

    const original = element.getAttribute(ORIGINAL_SIZE_ATTR);
    if (!original) return;
    const pixels = Number.parseFloat(original);
    const scaled = `${Math.round(pixels * scale * 100) / 100}px`;
    if (element.style.fontSize !== scaled) element.style.fontSize = scaled;
  });
}

export function observePublicPixelText(scale: number): () => void {
  const apply = () => {
    const main = document.querySelector('main');
    if (main) scalePublicPixelText(main, scale);
  };

  apply();
  if (scale <= 1) return () => {};

  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      apply();
    });
  });
  // Observe the document rather than an already-present <main>: public
  // campaigns arrive asynchronously from the API, while saved accessibility
  // preferences are applied immediately at app boot.
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
  return () => observer.disconnect();
}
